import React, { useCallback, useState, useEffect } from 'react';

import axios from 'axios';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FloatInput } from 'ui-components/components/float-input';

import { getUserFriendlyMessagesForErrorResponse } from '../../errors/user-friendly-error-message-service';
import { AuthenticationService } from '../../services/authentication-service';
import { gitData } from '../../constants/git-data';
import { CheckEnvService } from '../../services/check-env-service';
import LocalePicker from '../locale-picker';
import LogoutService from '../logout/logout-service';
import LoginService from './login-service';
import style from './login-form.less';
import messages from './messages';
import SystemStatusBanner from './system-status-banner';
import { CookieService } from '../../services/cookie-service';
import { bypassCaptcha, signupEmail } from '../../constants/cookies';
import { isValidMarketplace, buildMarketplaceAcceptUrl } from './marketplace-redirect-service';

const recaptchaSiteKey = window.SS_GLOBALS.frontEndConfig.recaptchaV2SiteKey;
const usingRecaptcha = !!recaptchaSiteKey && !CookieService.getCookieValue(bypassCaptcha) && window.SS_GLOBALS.useRecaptcha;
let recaptchaRef = null;
if (usingRecaptcha) {
  recaptchaRef = React.createRef();
}

const LoginForm = ({
  successfullyVerified = false,
  sessionExpired = false,
  isRedirectedFromMarketingSignup = false,
  marketplace,
  redirect,
  shouldRenderRememberMe = false,
  onLoginSuccess,
  showLoggedOutForMaintenance = false,
  customErrorMsg,
}) => {
  const emailFromCookie = CookieService.getCookieValue(signupEmail);
  const [username, setUsername] = useState(emailFromCookie || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errorDescriptor, setErrorDescriptor] = useState(undefined);
  const [loginButtonDisabled, setLoginButtonDisabled] = useState(false);
  const [loginSystemErrorCount, setLoginSystemErrorCount] = useState(0);

  const isProd = CheckEnvService.areWeInProdEnvironment();

  const incrementLoginSystemErrorCount = useCallback(() => {
    setLoginSystemErrorCount(loginSystemErrorCount + 1);
  }, [loginSystemErrorCount, setLoginSystemErrorCount]);

  const setErrorMessageDescriptor = useCallback(
    (errorMessageDescriptor) => {
      setErrorDescriptor(errorMessageDescriptor);
      setLoginButtonDisabled(false);
    },
    [setErrorDescriptor, setLoginButtonDisabled],
  );

  const getUserAndRedirect = useCallback(
    (overrideRedirect = undefined) => {
      const userClaimsCookie = LoginService.getCurrentUserInfo();

      if (userClaimsCookie) {
        return LoginService.redirect(overrideRedirect);
      }

      axios
        .get('/user/current')
        .then((response = {}) => {
          LoginService.setCurrentUserInfo(response?.data);
          LoginService.redirect(overrideRedirect || redirect);
        })
        .catch(() => {
          LoginService.redirect();
        });
    },
    [redirect],
  );

  const executeLoginWithCaptcha = () => {
    return recaptchaRef.current.execute();
  };

  const executeLogin = useCallback(
    (recaptchaToken) => {
      setLoginButtonDisabled(true);
      if (usingRecaptcha) {
        recaptchaRef.current.reset();
      }

      const isAuth0Authenticate = AuthenticationService.isAuth0Authenticated();
      if (isAuth0Authenticate) {
        // eslint-disable-next-line no-console
        console.error('previous auth0 session detected, logging out');
        LogoutService.logout('/logout');
        return;
      }

      const genericErrorMsgDescriptor = messages.genericErrorMsg;

      LoginService.getAuthToken({
        username,
        password,
        rememberMe,
        recaptchaToken,
      })
        .then(({ data }) => {
          if (!data) {
            // todo better error message here
            setErrorMessageDescriptor(messages.noData);
            return;
          }
          if (!data.success) {
            // todo better error message here
            setErrorMessageDescriptor(genericErrorMsgDescriptor);
            return;
          }

          if (emailFromCookie) {
            CookieService.removeCookie(signupEmail);
          }

          const redirectFromServer = LoginService.login(data);
          if (redirectFromServer) {
            return;
          }

          if (onLoginSuccess) {
            return onLoginSuccess();
          }

          if (isValidMarketplace(marketplace)) {
            const forwardParams = new URLSearchParams(window.location.search);
            getUserAndRedirect(buildMarketplaceAcceptUrl(marketplace, forwardParams));
            return;
          }

          axios
            .get('/user/loginBehavior')
            .then((loginBehavior) => {
              if (!loginBehavior.data.redirectUri) {
                const redir = window.location.href;
                getUserAndRedirect(redir);
                return;
              }
              getUserAndRedirect(loginBehavior.data.redirectUri);
            })
            .catch(() => {
              getUserAndRedirect();
            });
        })
        .catch((data) => {
          if (data?.statusCode === 500) {
            incrementLoginSystemErrorCount();
          }
          const stringifiedResponse = data?.responseText;

          if (!stringifiedResponse) {
            setErrorMessageDescriptor(genericErrorMsgDescriptor);
            return;
          }

          try {
            const responseObject = JSON.parse(stringifiedResponse);
            const { errors } = getUserFriendlyMessagesForErrorResponse({
              Errors: responseObject.Errors,
            });
            const loginError = errors[0];
            const { messageDescriptor, values } = loginError;
            setErrorMessageDescriptor({ ...messageDescriptor, values });
          } catch {
            setErrorMessageDescriptor(genericErrorMsgDescriptor);
          }
        });
    },
    [
      username,
      password,
      rememberMe,
      getUserAndRedirect,
      setErrorMessageDescriptor,
      incrementLoginSystemErrorCount,
      onLoginSuccess,
      emailFromCookie,
      marketplace,
    ],
  );

  const onLoginClicked = useCallback(() => {
    if (usingRecaptcha) {
      return executeLoginWithCaptcha();
    }

    executeLogin();
  }, [executeLogin]);

  useEffect(() => {
    if (sessionExpired) {
      setErrorMessageDescriptor(messages.sessionExpired);
    }
    const checkForEnterKey = (event) => {
      if (event.key === 'Enter') {
        onLoginClicked();
      }
    };
    document.body.addEventListener('keypress', checkForEnterKey);

    return () => {
      document.body.removeEventListener('keypress', checkForEnterKey);
    };
  }, [onLoginClicked, sessionExpired, setErrorMessageDescriptor]);

  return (
    <div className={style['login-form']}>
      {usingRecaptcha && <ReCAPTCHA ref={recaptchaRef} onChange={executeLogin} sitekey={recaptchaSiteKey} size='invisible' />}
      <div className={style['login-form-title']}>
        <FormattedMessage {...messages.logIntoShipstation} />
      </div>
      {!isProd && <div className={style['git-branch']}>{gitData}</div>}
      <SystemStatusBanner loginSystemErrorCount={loginSystemErrorCount} />
      {errorDescriptor && (
        <div className={style['error-message']}>
          <FormattedMessage {...errorDescriptor} />
        </div>
      )}
      {successfullyVerified && (
        <div className={style['info-message']}>
          <FormattedMessage {...messages.verificationSuccessful} />
        </div>
      )}
      {isRedirectedFromMarketingSignup && (
        <div className={style['info-message']}>
          <FormattedMessage {...messages.signupRedirectMessage} />
        </div>
      )}
      {showLoggedOutForMaintenance && (
        <div className={style['error-message']}>
          <FormattedMessage {...messages.sellerMaintenanceLogoutMessage} />
        </div>
      )}
      {customErrorMsg && <div className={style['error-message']}>{customErrorMsg}</div>}
      <div className={style['form-inputs']}>
        <div>
          <FloatInput
            autoFocus
            data-testid='login-username'
            label={<FormattedMessage {...messages.username} />}
            onChange={setUsername}
            value={username}
          />
        </div>
        <div>
          <FloatInput
            data-testid='login-password'
            isPassword
            label={<FormattedMessage {...messages.password} />}
            onChange={setPassword}
            type='password'
            value={password}
          />
        </div>
      </div>
      <div className={style['remember-me-and-login']}>
        <div className={style['remember-me']}>
          {shouldRenderRememberMe && (
            <Checkbox
              checked={rememberMe}
              label={<FormattedMessage {...messages.rememberMe} />}
              onChange={(newRememberMe) => setRememberMe(newRememberMe)}
            />
          )}
        </div>
        <Button disabled={loginButtonDisabled} onClick={onLoginClicked} testId='login-button' theme='primary'>
          <FormattedMessage {...messages.loginBtnText} />
        </Button>
      </div>
      <footer>
        <Button className={style['text-link']} href='//ship.shipstation.com/forgot-password' theme='link'>
          <FormattedMessage {...messages.forgotPassword} />
        </Button>
        <LocalePicker />
        {!isRedirectedFromMarketingSignup && (
          <Button className={style['text-link']} href='//www.shipstation.com/step1/' theme='link'>
            <FormattedMessage {...messages.tryShipstation} />
          </Button>
        )}
      </footer>
    </div>
  );
};

LoginForm.propTypes = {
  successfullyVerified: PropTypes.bool,
  sessionExpired: PropTypes.bool,
  isRedirectedFromMarketingSignup: PropTypes.bool,
  marketplace: PropTypes.string,
  redirect: PropTypes.string,
  shouldRenderRememberMe: PropTypes.bool,
  onLoginSuccess: PropTypes.func,
  showLoggedOutForMaintenance: PropTypes.bool,
  customErrorMsg: PropTypes.string,
};

export default LoginForm;
